import { useMemo, useRef, useState } from 'react';

import { Select } from '../../../components/ui';
import type { FieldControlProps } from '../../../components/ui/Field';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { useNguoiDungLoc } from '../hooks/useNguoiDungLoc';
import type { NguoiDungRutGon } from '../types';

/** Chờ người dùng ngừng gõ rồi mới tìm ở server. */
const CHO_GO_MS = 300;

export interface ChonNguoiDungProps {
  tenantId: string;
  nenTang: boolean;
  /** `user_id` đang lọc; có thể là id không có trong danh sách (đã xoá, của tenant khác). */
  value: string | undefined;
  onChange: (userId: string | undefined) => void;
  /** Dùng trong form: bề rộng và dây a11y của `Field`. Bỏ trống là ô lọc của nhật ký. */
  className?: string;
  fieldProps?: FieldControlProps;
}

/**
 * Ô lọc "Người dùng": chọn theo tên và email, giá trị là `user_id` (UUID).
 *
 * Cổng tenant tải cả danh sách một lần rồi lọc tại chỗ; cổng nền tảng tìm ở
 * server và thêm nhóm "Nhân sự nền tảng". Id đang lọc mà không có trong danh
 * sách (bấm từ một dòng của người đã rời tổ chức) vẫn hiện nguyên văn.
 */
export function ChonNguoiDung({ tenantId, nenTang, value, onChange, className = 'w-72 max-w-full', fieldProps }: ChonNguoiDungProps) {
  const { t } = useT();
  const dichLoi = useLoi();
  const [tim, setTim] = useState('');
  const hen = useRef<ReturnType<typeof setTimeout> | null>(null);
  const q = useNguoiDungLoc(tenantId, nenTang, tim);

  const goTim = (chuoi: string) => {
    if (!nenTang) return;
    if (hen.current) clearTimeout(hen.current);
    hen.current = setTimeout(() => setTim(chuoi), CHO_GO_MS);
  };

  const options = useMemo(() => {
    const muc = (u: NguoiDungRutGon) => ({
      value: u.user_id,
      // `label` là chuỗi để lọc tại chỗ và để ô hiện tên khi đã chọn.
      label: u.email ? `${u.display_name} · ${u.email}` : u.display_name,
    });
    const nhom = [
      ...(q.nguoiDung.length > 0
        ? [{ label: t('nhatKy.loc.nhomNguoiDung'), title: t('nhatKy.loc.nhomNguoiDung'), options: q.nguoiDung.map(muc) }]
        : []),
      ...(q.nhanSu.length > 0
        ? [{ label: t('nhatKy.loc.nhomNhanSu'), title: t('nhatKy.loc.nhomNhanSu'), options: q.nhanSu.map(muc) }]
        : []),
    ];
    const coSan = [...q.nguoiDung, ...q.nhanSu].some((u) => u.user_id === value);
    return value && !coSan ? [{ value, label: value }, ...nhom] : nhom;
  }, [q.nguoiDung, q.nhanSu, value, t]);

  return (
    <Select<string>
      {...fieldProps}
      className={className}
      allowClear
      showSearch
      aria-label={t('nhatKy.loc.nguoi')}
      placeholder={t('nhatKy.loc.nguoiGoiY')}
      value={value}
      options={options}
      loading={q.dangTai}
      // Cổng nền tảng tìm ở server: tắt lọc tại chỗ để không lọc hai lần.
      filterOption={nenTang ? false : (input, opt) => String(opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
      onSearch={goTim}
      onChange={(v) => onChange(v ?? undefined)}
      onClear={() => goTim('')}
      notFoundContent={
        q.loi ? (
          <span role="alert" className="text-xs text-danger-700">
            {t('nhatKy.loc.loiNguoi', { loi: dichLoi(q.loi) })}
          </span>
        ) : q.dangTai ? (
          <span className="text-xs text-content-muted">{t('nhatKy.loc.dangTaiNguoi')}</span>
        ) : (
          <span className="text-xs text-content-muted">{t('nhatKy.loc.khongCoNguoi')}</span>
        )
      }
    />
  );
}
